/**
 * The library's `cva`. Identical call surface to class-variance-authority's,
 * with one addition: the returned composer carries its config object as a
 * readable `.config` property. cva itself closes over the config, which makes
 * a composer's variant axes invisible at runtime — and the axes ARE the
 * component's visual contract. Attaching the config lets machinery (the docs
 * generator's variant matrix, a lock derivation) enumerate every axis, value
 * and default from the same object production composes classes from, with no
 * extracted copy to drift.
 *
 * Every `cva(...)` in this package goes through this module, never the
 * package directly — a composer built on the raw import is a contract the
 * machine cannot read.
 */

import { cva as composeCva } from "class-variance-authority";

export type { VariantProps } from "class-variance-authority";

/** The runtime shape of a composer's config, as the docs machinery reads it. */
export type VariantConfig = {
  variants?: Record<string, Record<string, unknown>>;
  defaultVariants?: Record<string, unknown>;
};

export const cva: typeof composeCva = (base, config) =>
  Object.assign(composeCva(base, config), { config });

/**
 * The config a composer was built with, or undefined for a value that is not
 * a config-carrying composer. The one sanctioned way to read `.config` —
 * callers never reach into the property untyped.
 */
export function variantConfig(composer: unknown): VariantConfig | undefined {
  if (typeof composer !== "function" || !("config" in composer)) {
    return undefined;
  }
  const config = (composer as { config: unknown }).config;
  return typeof config === "object" && config !== null
    ? (config as VariantConfig)
    : undefined;
}
